import { useCallback } from 'react';
import { authState, LmsRoles, useRefreshTokenMutation } from 'core';
import { saveToken } from 'services/AuthService';

type RefreshTokenHookType = {
  updateTokens: (oldRefreshToken: string, role?: LmsRoles) => Promise<boolean>;
  loading: boolean;
};

const loggedOutState = {
  isLoggedIn: false,
  access_token: null,
  refresh_token: null,
  isTokenRefreshed: true,
  active_role: null,
  isDemoUser: false,
};

export const useRefreshToken = (): RefreshTokenHookType => {
  const [refreshTokenMutation, { loading }] = useRefreshTokenMutation({});

  // The new access_token must be committed to authState before this resolves:
  // callers reset the Apollo store right after, and middlewareLink reads
  // authState synchronously when it sends the refetches.
  const updateTokens = useCallback(
    async (oldRefreshToken: string, role?: LmsRoles) => {
      try {
        const { data } = await refreshTokenMutation({
          variables: {
            refresh_token: oldRefreshToken,
            scope: process.env.REACT_APP_SCOPE || 'LMS',
            activeRole: role,
          },
        });

        const result = data?.refreshToken;

        if (!result || result.errors || !result.token || !result.refreshToken) {
          authState(loggedOutState);
          return false;
        }

        authState({
          isLoggedIn: true,
          access_token: result.token,
          refresh_token: result.refreshToken,
          isTokenRefreshed: true,
          active_role: result.payload.active_role,
          isDemoUser: result.payload.email === process.env.REACT_APP_DEMO_USER,
        });
        saveToken(result.refreshToken);

        return true;
      } catch (e) {
        authState(loggedOutState);
        return false;
      }
    },
    [refreshTokenMutation],
  );

  return {
    updateTokens,
    loading,
  };
};
